import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Select, InputLabel, Radio, RadioGroup, FormControl, FormControlLabel, FormLabel, InputAdornment, IconButton, TextField, MenuItem, Autocomplete, Box } from '@mui/material';
import HelpCenter from '@mui/icons-material/HelpCenter';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DoneIcon from '@mui/icons-material/Done';
import useAxiosProtect from '../../../../hooks/useAxiosProtect';

const TargetModal = props => {
    var init = useRef(true);
    const axiosProtect = useAxiosProtect();

    const [page, setPage] = useState(1);

    const floatRegex = /^(-?\d*\.?\d*)?$/;

    const [serverError, setServerError] = useState('');

    const [firstPageValues, setFirstPageValues] = useState({
        topLevelError: '',
        title: '',
        maxTitleLength: 32,
        targetLevel: null,
        allowedLevels: {org: false, site: false},
        availableOrgs: [],
        availableSites: [],
        selectedOrgID: {value: null, error: ''},
        selectedSiteID: {value: null, error: ''},
        entryMethod: null,
        manualYears: Array.from({length: new Date().getFullYear() - 2010}, (_, i) => 2010 + i),
        baselineYear: {value: '', error: ''},
    });

    const [reductionTargetsForm, setReductionTargetsForm] = useState({
        targetYear: {value: '', error: ''},
        waterReductionPercent: {value: '', error: ''},
        electricityReductionPercent: {value: '', error: ''},
        gasReductionPercent: {value: '', error: ''},
        ghgReductionPercent: {value: '', error: ''},
        costReductionPercent: {value: '', error: ''}
    });

    const [baselineForm, setBaselineForm] = useState({
        water: {
            amount: {value: '', error: ''},
            unitCost: {value: '', error: ''}
        },
        electricity: {
            amount: {value: '', error: ''},
            unitCost: {value: '', error: ''}
        },
        gas: {
            amount: {value: '', error: ''},
            unitCost: {value: '', error: ''}
        }
    });

    const [units, setUnits] = useState('m3');
    const [gasUnitString, setGasUnitString] = useState(<span>m<sup>3</sup></span>)

    const minTargetYear = 2021;
    const targetYears = Array.from({ length: 2060 - minTargetYear }, (_, i) => (minTargetYear) + i);

    const handleUnits = (event) => {
        setUnits(event.target.value);
    }

    useEffect(() => {
        const getPermissions = async () => {
            try{
                let response;
                
                response = await axiosProtect.get(`/tg/targets/target-permissions?uid=${props.userData.id}`);

                let permissions = {...response.data.permissions};

                let orgs = permissions.orgs.sort((a, b) => a.name.localeCompare(b.name));
                let sites = permissions.sites.sort((a, b) => a.name.localeCompare(b.name));

                if(props.targetModalState === 'edit'){
                    let orgValue = null;
                    let siteValue = null;

                    if(props.targetToEdit.site_id){
                        let orgIDTemp = '';

                        for(let i = 0;i<sites.length;++i){
                            if(sites[i].id == props.targetToEdit.site_id){
                                siteValue = sites[i];
                                orgIDTemp = sites[i].org_id;
                                break;
                            }
                        }

                        for(let i = 0;i<orgs.length;++i){
                            if(orgs[i]._id === orgIDTemp){
                                orgValue = orgs[i];
                                break;
                            }
                        }
                    }else if(props.targetToEdit.org_id){
                        for(let i = 0;i<orgs.length;++i){
                            if(orgs[i]._id === props.targetToEdit.org_id){
                                orgValue = orgs[i];
                                break;
                            }
                        }
                    }

                    setFirstPageValues(prev => ({
                        ...prev,
                        title: props.targetToEdit.title,
                        targetLevel: props.targetToEdit.site_id ? 'site' : 'org',
                        allowedLevels: {org: permissions.org_level, site: permissions.site_level},
                        availableOrgs: [...orgs],
                        availableSites: [...sites],
                        selectedOrgID: {value: orgValue, error: ''},
                        selectedSiteID: {value: siteValue, error: ''},
                        entryMethod: 'manual',
                        manualYears: Array.from({length: new Date().getFullYear() - 2010}, (_, i) => 2010 + i),
                        baselineYear: {value: `${props.targetToEdit.baseline_year}`, error: ''}
                    }));

                    setReductionTargetsForm(prev => ({
                        ...prev,
                        targetYear: {value: props.targetToEdit.reduction_target_year, error: ''},
                        waterReductionPercent: {value: `${props.targetToEdit.baseline_water_reduction_pct * 100}`, error: ''},
                        electricityReductionPercent: {value: `${props.targetToEdit.baseline_elec_reduction_pct * 100}`, error: ''},
                        gasReductionPercent: {value: `${props.targetToEdit.baseline_gas_reduction_pct * 100}`, error: ''},
                        ghgReductionPercent: {value: `${props.targetToEdit.reduction_target_ghg_percent * 100}`, error: ''},
                        costReductionPercent: {value: `${props.targetToEdit.reduction_target_cost_percent * 100}`, error: ''}
                    }));

                    setBaselineForm(prev => ({
                        ...prev,
                        water: {
                            amount: {value: `${props.targetToEdit.baseline_water_m3}`, error: ''},
                            unitCost: {value: `${props.targetToEdit.baseline_water_unit_cost}`, error: ''}
                        },
                        electricity: {
                            amount: {value: `${props.targetToEdit.baseline_elec_kwh}`, error: ''},
                            unitCost: {value: `${props.targetToEdit.baseline_elec_unit_cost}`, error: ''}
                        },
                        gas: {
                            amount: {value: `${props.targetToEdit.baseline_gas_usage}`, error: ''},
                            unitCost: {value: `${props.targetToEdit.gas_unit_cost}`, error: ''}
                        }
                    }));

                    setUnits(props.targetToEdit.gas_units);

                }else{
                    setFirstPageValues(prev => ({
                        ...prev,
                        targetLevel: permissions.org_level ? 'org' : permissions.site_level ? 'site' : null,
                        availableOrgs: [...orgs],
                        availableSites: [...sites],
                        entryMethod: 'manual',
                        allowedLevels: {org: permissions.org_level, site: permissions.site_level}
                    }));
                }
            }catch(err) {
                console.error("Error:", err.response?.data || err.message);
            }
        }
        
        if(init.current){
            init.current = false;

            if(props.targetModalState !== 'delete'){
                getPermissions();
            }
        }
        
    });

    useEffect(() => {
        if(units === 'm3'){
            setGasUnitString(<span>m<sup>3</sup></span>)
        }else{
            setGasUnitString(units.toUpperCase())
        }
    }, [units]);

    const submitNewTarget = () => {
        let payload = {
            //metadata
            title: firstPageValues.title,
            input_method: firstPageValues.entryMethod,
            baseline_year: parseInt(firstPageValues.baselineYear.value),

            //targets
            reduction_target_year: reductionTargetsForm.targetYear.value,
            reduction_target_ghg_percent: parseFloat(reductionTargetsForm.ghgReductionPercent.value),
            reduction_target_cost_percent: parseFloat(reductionTargetsForm.costReductionPercent.value),
            
            baseline_elec_reduction_pct: parseFloat(reductionTargetsForm.electricityReductionPercent.value),
            baseline_water_reduction_pct: parseFloat(reductionTargetsForm.waterReductionPercent.value),
            baseline_gas_reduction_pct: parseFloat(reductionTargetsForm.gasReductionPercent.value),
        };

        if(firstPageValues.targetLevel === 'org'){
            payload.is_org_wide = true;
            payload.org_id = firstPageValues.selectedOrgID.value._id;
        }else{
            payload.is_org_wide = false;
            payload.site_id = firstPageValues.selectedSiteID.value.id;
        }

        if(firstPageValues.entryMethod === 'manual'){
            payload.baseline_water_m3 = parseFloat(baselineForm.water.amount.value);
            payload.baseline_water_unit_cost = parseFloat(baselineForm.water.unitCost.value);

            payload.baseline_elec_kwh = parseFloat(baselineForm.electricity.amount.value);
            payload.baseline_elec_unit_cost = parseFloat(baselineForm.electricity.unitCost.value);

            payload.baseline_gas_usage = parseFloat(baselineForm.gas.amount.value);
            payload.gas_unit_cost = parseFloat(baselineForm.gas.unitCost.value);
            payload.gas_units = units;
        }

        if(props.targetModalState === 'edit'){
            axiosProtect.put(`/tg/targets?targetID=${props.targetToEdit.id}&displayID=${parseInt(window.location.href.split('/').slice(-1))}`, payload).then(response => {
                setServerError('');

                let siteName = response.data.updatedTarget.is_org_wide ? 'org-wide-targets' : response.data.updatedTarget.site_name;
                let siteIndex = -1;
                let targetIndex = -1;

                for(let i = 0;i<props.targetReadings.length;++i){
                    if(props.targetReadings[i].site_name === siteName){
                        siteIndex = i;

                        for(let j = 0;j<props.targetReadings[i].targets.length;++j){
                            if(props.targetReadings[i].targets[j].id == response.data.updatedTarget.id){
                                targetIndex = j;
                                break;
                            }
                        }
                    }
                }

                let targetCopy = [...props.targetReadings];
                targetCopy[siteIndex].targets[targetIndex] = {...response.data.updatedTarget};

                props.setTargetReadings([...targetCopy]);

                props.showSuccessMessage(true);
                props.closeModal();
            }).catch(error =>{
                setServerError(error.response.data.error);
            });
        }else{
            axiosProtect.post(`/tg/targets?displayID=${parseInt(window.location.href.split('/').slice(-1))}`, payload).then(response => {
                setServerError('');

                if(response.data.newTarget != null){
                    if(response.data.newTarget.is_org_wide){
                        let hasOrgLevel = false, orgIndex = -1;

                        for(let i = 0;i<props.targetReadings.length;++i){
                            if(props.targetReadings[i].site_name === 'org-wide-targets'){
                                hasOrgLevel = true;
                                orgIndex = i;
                                break;
                            }
                        }

                        if(hasOrgLevel){
                            props.setTargetReadings(prev => {
                                const updated = [...prev];
                                const updatedTargets = [...updated[orgIndex].targets, response.data.newTarget];

                                updated[orgIndex] = {
                                    ...updated[orgIndex],
                                    targets: updatedTargets
                                }

                                return updated;
                            });
                        }else{
                            props.setTargetReadings(prev => {
                                const updated = [...prev];
                            
                                updated.push({
                                    site_name: 'org-wide-targets',
                                    targets: [response.data.newTarget]
                                });
                            
                                return updated;
                            });
                        }

                    }else{
                        let hasSite = false, siteIndex = -1;

                        for(let i = 0;i<props.targetReadings.length;++i){
                            if(props.targetReadings[i].site_name === response.data.newTarget.site_name){
                                hasSite = true;
                                siteIndex = i;
                                break;
                            }
                        }

                        if(hasSite){
                            props.setTargetReadings(prev => {
                                const updated = [...prev];
                                const updatedTargets = [...updated[siteIndex].targets, response.data.newTarget];

                                updated[siteIndex] = {
                                    ...updated[siteIndex],
                                    targets: updatedTargets
                                }

                                return updated;
                            });
                        }else{
                            props.setTargetReadings(prev => {
                                const updated = [...prev];
                            
                                updated.push({
                                site_name: response.data.newTarget.site_name,
                                targets: [response.data.newTarget]
                                });
                            
                                return updated;
                            });
                        }
                    }
                }

                props.showSuccessMessage(false);
                props.closeModal();
            }).catch(error =>{
                setServerError(error.response.data.error);
            });
        }
    }

    const incrementPage = () => {
        if(page === 1 && validateFirstPage()){
            setPage(2);
        }else if(page === 2 && validateReductionTargets()){
            if(firstPageValues.entryMethod === 'manual'){
                setPage(3);
            }else{
                submitNewTarget();
            }
        }else if(page === 3 && validateBaselineData()){
            submitNewTarget();
        }
    };
    
    const validateFirstPage = () => {
        let isValid = true;

        let formCopy = {...firstPageValues};

        if(formCopy.targetLevel == null || formCopy.entryMethod == null){
            formCopy.topLevelError = 'Cannot process form';
            isValid = false;
        }else{
            formCopy.topLevelError = '';
        }

        if(formCopy.selectedOrgID.value == null){
            formCopy.selectedOrgID.error = 'Required';
            isValid = false;
        }else{
            formCopy.selectedOrgID.error = '';
        }

        if(formCopy.targetLevel === 'site' && formCopy.selectedSiteID.value == null){
            formCopy.selectedSiteID.error = 'Required';
            isValid = false;
        }else{
            formCopy.selectedSiteID.error = '';
        }

        if(formCopy.baselineYear.value === ''){
            formCopy.baselineYear.error = 'Required';
            isValid = false;
        }else{
            formCopy.baselineYear.error = '';
        }

        setFirstPageValues({...formCopy});

        return isValid;
    }

    const validateReductionTargets = () => {
        let isValid = true;

        let formCopy = {...reductionTargetsForm};

        if(formCopy.targetYear.value === ''){
            formCopy.targetYear.error = 'Required';
            isValid = false;
        }else if(parseInt(formCopy.targetYear.value) <= parseInt(firstPageValues.baselineYear.value)){
            formCopy.targetYear.error = `Must be greater than baseline year (${firstPageValues.baselineYear.value})`;
            isValid = false;
        }else{
            formCopy.targetYear.error = '';
        }

        let waterValue = formCopy.waterReductionPercent.value.trim();
        if(waterValue === ''){
            formCopy.waterReductionPercent.error = 'Required, enter 0 if N/A';
            isValid = false;
        }else if(isNaN(parseFloat(waterValue))){
            formCopy.waterReductionPercent.error = 'Invalid entry';
            isValid = false;
        }else if(parseFloat(waterValue) < 0 || parseFloat(waterValue) > 100){
            formCopy.waterReductionPercent.error = 'Must be between 0 and 100 (inclusive)';
            isValid = false;
        }else{
            formCopy.waterReductionPercent.error = '';
        }

        let electricityValue = formCopy.electricityReductionPercent.value.trim();
        if(electricityValue === ''){
            formCopy.electricityReductionPercent.error = 'Required, enter 0 if N/A';
            isValid = false;
        }else if(isNaN(parseFloat(electricityValue))){
            formCopy.electricityReductionPercent.error = 'Invalid entry';
            isValid = false;
        }else if(parseFloat(electricityValue) < 0 || parseFloat(electricityValue) > 100){
            formCopy.electricityReductionPercent.error = 'Must be between 0 and 100 (inclusive)';
            isValid = false;
        }else{
            formCopy.electricityReductionPercent.error = '';
        }

        let gasValue = formCopy.gasReductionPercent.value.trim();
        if(gasValue === ''){
            formCopy.gasReductionPercent.error = 'Required, enter 0 if N/A';
            isValid = false;
        }else if(isNaN(parseFloat(gasValue))){
            formCopy.gasReductionPercent.error = 'Invalid entry';
            isValid = false;
        }else if(parseFloat(gasValue) < 0 || parseFloat(gasValue) > 100){
            formCopy.gasReductionPercent.error = 'Must be between 0 and 100 (inclusive)';
            isValid = false;
        }else{
            formCopy.gasReductionPercent.error = '';
        }

        let ghgValue = formCopy.ghgReductionPercent.value.trim();
        if(ghgValue === ''){
            formCopy.ghgReductionPercent.error = 'Required, enter 0 if N/A';
            isValid = false;
        }else if(isNaN(parseFloat(ghgValue))){
            formCopy.ghgReductionPercent.error = 'Invalid entry';
            isValid = false;
        }else if(parseFloat(ghgValue) < 0 || parseFloat(ghgValue) > 100){
            formCopy.ghgReductionPercent.error = 'Must be between 0 and 100 (inclusive)';
            isValid = false;
        }else{
            formCopy.ghgReductionPercent.error = '';
        }

        let costValue = formCopy.costReductionPercent.value.trim();
        if(costValue === ''){
            formCopy.costReductionPercent.error = 'Required, enter 0 if N/A';
            isValid = false;
        }else if(isNaN(parseFloat(costValue))){
            formCopy.costReductionPercent.error = 'Invalid entry';
            isValid = false;
        }else if(parseFloat(costValue) < 0 || parseFloat(costValue) > 100){
            formCopy.costReductionPercent.error = 'Must be between 0 and 100 (inclusive)';
            isValid = false;
        }else{
            formCopy.costReductionPercent.error = '';
        }

        setReductionTargetsForm({...formCopy});

        return isValid;
    }

    const validateBaselineData = () => {
        let isValid = true;

        let formCopy = {...baselineForm};

        let waterAmount = formCopy.water.amount.value, waterCost = formCopy.water.unitCost.value;
        if(waterAmount.trim() === ''){
            formCopy.water.amount.error = 'Required, enter 0 if N/A';
            isValid = false;

        }else if(isNaN(parseFloat(waterAmount))){
            formCopy.water.amount.error = 'Invalid input';
            isValid = false;
        }else if(parseFloat(waterAmount) < 0){
            formCopy.water.amount.error = 'Must be >= 0';
            isValid = false;
        }else{
            formCopy.water.amount.error = '';
        }

        if(waterCost.trim() === ''){
            formCopy.water.unitCost.error = 'Required, enter 0 if N/A';
            isValid = false;

        }else if(isNaN(parseFloat(waterCost))){
            formCopy.water.unitCost.error = 'Invalid input';
            isValid = false;
        }else if(parseFloat(waterCost) < 0){
            formCopy.water.unitCost.error = 'Must be >= 0';
            isValid = false;
        }else{
            formCopy.water.unitCost.error = '';
        }

        let electricityAmount = formCopy.electricity.amount.value, electricityCost = formCopy.electricity.unitCost.value;
        if(electricityAmount.trim() === ''){
            formCopy.electricity.amount.error = 'Required, enter 0 if N/A';
            isValid = false;

        }else if(isNaN(parseFloat(electricityAmount))){
            formCopy.electricity.amount.error = 'Invalid input';
            isValid = false;
        }else if(parseFloat(electricityAmount) < 0){
            formCopy.electricity.amount.error = 'Must be >= 0';
            isValid = false;
        }else{
            formCopy.electricity.amount.error = '';
        }

        if(electricityCost.trim() === ''){
            formCopy.electricity.unitCost.error = 'Required, enter 0 if N/A';
            isValid = false;

        }else if(isNaN(parseFloat(electricityCost))){
            formCopy.electricity.unitCost.error = 'Invalid input';
            isValid = false;
        }else if(parseFloat(electricityCost) < 0){
            formCopy.electricity.unitCost.error = 'Must be >= 0';
            isValid = false;
        }else{
            formCopy.electricity.unitCost.error = '';
        }

        let gasAmount = formCopy.gas.amount.value, gasCost = formCopy.gas.unitCost.value;
        if(gasAmount.trim() === ''){
            formCopy.gas.amount.error = 'Required, enter 0 if N/A';
            isValid = false;

        }else if(isNaN(parseFloat(gasAmount))){
            formCopy.gas.amount.error = 'Invalid input';
            isValid = false;
        }else if(parseFloat(gasAmount) < 0){
            formCopy.gas.amount.error = 'Must be >= 0';
            isValid = false;
        }else{
            formCopy.gas.amount.error = '';
        }

        if(gasCost.trim() === ''){
            formCopy.gas.unitCost.error = 'Required, enter 0 if N/A';
            isValid = false;

        }else if(isNaN(parseFloat(gasCost))){
            formCopy.gas.unitCost.error = 'Invalid input';
            isValid = false;
        }else if(parseFloat(gasCost) < 0){
            formCopy.gas.unitCost.error = 'Must be >= 0';
            isValid = false;
        }else{
            formCopy.gas.unitCost.error = '';
        }

        setBaselineForm({...formCopy});

        return isValid;
    }

    return (
        <Modal
            show={props.targetModalState !== ''}
            onHide={() => {
                props.closeModal();
            }}
            backdrop="static"
            centered
            className="opp-modal-root"
            backdropClassName="opp-modal-backdrop"
            dialogClassName="opp-modal-cw"
        >
            <Modal.Header closeButton>
                <h4>{props.targetModalState.substring(0,1).toUpperCase()}{props.targetModalState.substring(1)} target</h4>
            </Modal.Header>

            {
                props.targetModalState === 'delete' && 
                <Modal.Body style={{textAlign:'center'}}>
                    <p>Are you sure you want to delete this target?</p>
                    <p style={{margin:'0'}}>
                        {
                            props.targetToDelete.title === '' ?
                            <i>untitled</i>
                            :
                            <strong>{props.targetToDelete.title}</strong>
                        }
                    </p>
                </Modal.Body>
            }

            {
                (props.targetModalState === 'add' || props.targetModalState === 'edit') && 
                <Modal.Body>
                    {
                        page === 1 &&
                        <>
                            {firstPageValues.topLevelError !== '' && <small className="text-error">{firstPageValues.topLevelError}</small>}

                            <TextField
                                fullWidth
                                label="Title (optional)"
                                style={{marginBottom: '2.5%'}}
                                value={firstPageValues.title}
                                helperText={`${firstPageValues.maxTitleLength - firstPageValues.title.length} character${firstPageValues.maxTitleLength - firstPageValues.title.length === 1 ? '' : 's'} left`}
                                onChange={e => {
                                    if(e.target.value.length > firstPageValues.maxTitleLength) return;

                                    setFirstPageValues(prev => ({
                                        ...prev,
                                        title: e.target.value
                                    }));
                                }}
                            />

                            <br/> 

                            <FormControl style={{display: "flex", alignItems: "center"}}>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="row-radio-buttons-group"
                                    value={firstPageValues.targetLevel}
                                    onChange={e => {
                                        setFirstPageValues(prev => ({
                                            ...prev,
                                            targetLevel: e.target.value,
                                            selectedSiteID: {
                                                value: null,
                                                error: firstPageValues.selectedSiteID.error
                                            },
                                            baselineYear: {
                                                value: '',
                                                error: firstPageValues.baselineYear.error
                                            }
                                        }));
                                    }}
                                >
                                    <FormControlLabel value="org" control={<Radio disabled={!firstPageValues.allowedLevels.org}/>} label="Organization-level target" />
                                    <FormControlLabel value="site" control={<Radio disabled={!firstPageValues.allowedLevels.site}/>} label="Site-level target" />
                                </RadioGroup>
                            </FormControl>

                            <br/>
                            
                            <Autocomplete
                                disablePortal
                                id="tg-red-org-autocomplete"
                                fullWidth
                                options={firstPageValues.availableOrgs}
                                autoHighlight
                                value={firstPageValues.selectedOrgID.value}
                                onChange={(_, option) => {
                                    setFirstPageValues(prev => ({
                                        ...prev,
                                        selectedOrgID: {
                                            value: option,
                                            error: ''
                                        },
                                        selectedSiteID: {
                                            value: null,
                                            error: firstPageValues.selectedSiteID.error
                                        },
                                        baselineYear: {
                                            value: '',
                                            error: firstPageValues.baselineYear.error
                                        }
                                    }));
                                }}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                    <Box
                                        key={key}
                                        value={option._id}
                                        component="li"
                                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                        {...optionProps}
                                    >
                                        {option.name}
                                    </Box>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Organization"
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'new-password', // disable autocomplete and autofill
                                        }}
                                    />
                                )}
                            />
                            {firstPageValues.selectedOrgID.error !== '' && <small className="text-error">{firstPageValues.selectedOrgID.error}</small>}
                            
                            <br/>

                            {
                                firstPageValues.targetLevel === 'site' && 
                                <>
                                    <Autocomplete
                                        disablePortal
                                        id="tg-red-site-autocomplete"
                                        fullWidth
                                        options={
                                            firstPageValues.selectedOrgID.value == null ? 
                                            [] : firstPageValues.availableSites.filter(site => site.org_id === firstPageValues.selectedOrgID.value?._id)
                                        }
                                        autoHighlight
                                        value={firstPageValues.selectedSiteID.value}
                                        onChange={(_, option) => {
                                            setFirstPageValues(prev => ({
                                                ...prev,
                                                selectedSiteID: {
                                                    value: option,
                                                    error: ''
                                                },
                                                baselineYear: {
                                                    value: '',
                                                    error: firstPageValues.baselineYear.error
                                                }
                                            }));
                                        }}
                                        getOptionLabel={(option) => option.name}
                                        renderOption={(props, option) => {
                                            const { key, ...optionProps } = props;
                                            return (
                                            <Box
                                                key={key}
                                                value={option.id}
                                                component="li"
                                                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                                {...optionProps}
                                            >
                                                {option.name}
                                            </Box>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Site"
                                                inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: 'new-password', // disable autocomplete and autofill
                                                }}
                                            />
                                        )}
                                    />
                                    {firstPageValues.selectedSiteID.error !== '' && <small className="text-error">{firstPageValues.selectedSiteID.error}</small>}

                                    <br/>
                                </>
                            }

                            <FormControl style={{display: "flex", alignItems: "center"}}>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="entry-method-group"
                                    value={firstPageValues.entryMethod}
                                    onChange={e => {
                                        setFirstPageValues(prev => ({
                                            ...prev,
                                            entryMethod: e.target.value,
                                            baselineYear: {
                                                value: '',
                                                error: firstPageValues.baselineYear.error
                                            }
                                        }))
                                    }}
                                >
                                    <FormControlLabel value="bills" control={<Radio />} label="From Utility Bills" />
                                    <FormControlLabel value="manual" control={<Radio />} label="Manual Entry" />
                                </RadioGroup>
                            </FormControl>
                            
                            <br/>
                            
                            <FormControl fullWidth variant="outlined" style={{ marginBottom: '1.25vh' }}>
                                <InputLabel id="baseline-year-label">Baseline Year</InputLabel>
                                <Select
                                    labelId="baseline-year-label"
                                    id="baseline-year-select"
                                    label="Baseline Year"
                                    variant="outlined"
                                    value={firstPageValues.baselineYear.value}
                                    onChange={e => {
                                        setFirstPageValues(prev => ({
                                            ...prev,
                                            baselineYear: {
                                            value: e.target.value,
                                            error: prev.baselineYear.error,
                                            }
                                        }));
                                    }}
                                    MenuProps={{ disablePortal: true }}
                                >
                                {
                                    (firstPageValues.entryMethod === 'bills' && firstPageValues.selectedSiteID.value != null) ? 
                                        firstPageValues.selectedSiteID.value.autoFillYears.map((year, i) => (
                                            <MenuItem key={i} value={`${year}`}>{year}</MenuItem>
                                        ))
                                    : 
                                    (firstPageValues.entryMethod === 'bills' && firstPageValues.selectedOrgID.value != null) ? 
                                        firstPageValues.selectedOrgID.value.autoFillYears.map((year, i) => (
                                            <MenuItem key={i} value={`${year}`}>{year}</MenuItem>
                                        ))
                                    : 
                                    firstPageValues.manualYears.map((year, i) => (
                                        <MenuItem key={i} value={`${year}`}>{year}</MenuItem>
                                    ))
                                }
                                </Select>
                            </FormControl>
                            {firstPageValues.baselineYear.error !== '' && <small className="text-error">{firstPageValues.baselineYear.error}</small>}

                            <br/>
                        </>
                        } 

                        {page === 2 &&
                        <>  
                            {serverError !== '' && <small className="text-error">{serverError}</small>}

                            <h5>Reduction Targets</h5>

                            <br/>

                            <FormControl fullWidth className="w-full" error={reductionTargetsForm.targetYear.error !== ''}>
                                <InputLabel id="target-year-label">Target Year</InputLabel>
                                <Select
                                    labelId="target-year-label"
                                    id="target-year-select"
                                    value={reductionTargetsForm.targetYear.value}
                                    onChange={e => {
                                        setReductionTargetsForm(prev => ({
                                            ...prev,
                                            targetYear: {value: e.target.value, error: prev.targetYear.error}
                                        }));
                                    }}
                                    label="Target Year"
                                    MenuProps={{ disablePortal: true }}
                                >
                                    {targetYears.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {reductionTargetsForm.targetYear.error !== '' && <small className="text-error">{reductionTargetsForm.targetYear.error}</small>}

                            <br/>
                            <br/>

                            <TextField
                                fullWidth
                                label="Water reduction target"
                                error={reductionTargetsForm.waterReductionPercent.error !== ''}
                                helperText={reductionTargetsForm.waterReductionPercent.error}
                                style={{marginBottom:'1.25vh'}}
                                value={reductionTargetsForm.waterReductionPercent.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setReductionTargetsForm(prev => ({
                                        ...prev,
                                        waterReductionPercent: {value: e.target.value, error: prev.waterReductionPercent.error}
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label="Electricity reduction target"
                                error={reductionTargetsForm.electricityReductionPercent.error !== ''}
                                helperText={reductionTargetsForm.electricityReductionPercent.error}
                                style={{marginBottom:'1.25vh'}}
                                value={reductionTargetsForm.electricityReductionPercent.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;
                                    
                                    setReductionTargetsForm(prev => ({
                                        ...prev,
                                        electricityReductionPercent: {value: e.target.value, error: prev.electricityReductionPercent.error}
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label="Gas reduction target"
                                error={reductionTargetsForm.gasReductionPercent.error !== ''}
                                helperText={reductionTargetsForm.gasReductionPercent.error}
                                style={{marginBottom:'1.25vh'}}
                                value={reductionTargetsForm.gasReductionPercent.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;
                                    
                                    setReductionTargetsForm(prev => ({
                                        ...prev,
                                        gasReductionPercent: {value: e.target.value, error: prev.gasReductionPercent.error}
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label="GHG reduction target"
                                error={reductionTargetsForm.ghgReductionPercent.error !== ''}
                                helperText={reductionTargetsForm.ghgReductionPercent.error}
                                style={{marginBottom:'1.25vh'}}
                                value={reductionTargetsForm.ghgReductionPercent.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;
                                    
                                    setReductionTargetsForm(prev => ({
                                        ...prev,
                                        ghgReductionPercent: {value: e.target.value, error: prev.ghgReductionPercent.error}
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label="Cost reduction target"
                                error={reductionTargetsForm.costReductionPercent.error !== ''}
                                helperText={reductionTargetsForm.costReductionPercent.error}
                                value={reductionTargetsForm.costReductionPercent.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;
                                    
                                    setReductionTargetsForm(prev => ({
                                        ...prev,
                                        costReductionPercent: {value: e.target.value, error: prev.costReductionPercent.error}
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />
                        </>                                
                    } { page === 3 &&
                        <>  
                            {serverError !== '' && <small className="text-error">{serverError}</small>}

                            <h5>Baseline Consumption</h5>

                            <p>Water</p>

                            <TextField
                                fullWidth
                                label={<span>Baseline water usage</span>}
                                error={baselineForm.water.amount.error !== ''}
                                helperText={baselineForm.water.amount.error}
                                style={{marginBottom:'1.25vh'}}
                                value={baselineForm.water.amount.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setBaselineForm(prev => ({
                                        ...prev,
                                        water: {
                                            ...prev.water,
                                            amount: {value: e.target.value, error: prev.water.amount.error}
                                        }
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">m<sup>3</sup></InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label={<span>Water unit cost</span>}
                                error={baselineForm.water.unitCost.error !== ''}
                                helperText={baselineForm.water.unitCost.error}
                                style={{marginBottom:'1.25vh'}}
                                value={baselineForm.water.unitCost.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setBaselineForm(prev => ({
                                        ...prev,
                                        water: {
                                            ...prev.water,
                                            unitCost: {value: e.target.value, error: prev.water.unitCost.error}
                                        }
                                    }))
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/m<sup>3</sup></InputAdornment>,
                                }}
                            />

                            <br/>

                            <p>Electricity</p>

                            <TextField
                                fullWidth
                                label={<span>Baseline electricity usage</span>}
                                error={baselineForm.electricity.amount.error !== ''}
                                helperText={baselineForm.electricity.amount.error}
                                style={{marginBottom:'1.25vh'}}
                                value={baselineForm.electricity.amount.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setBaselineForm(prev => ({
                                        ...prev,
                                        electricity: {
                                            ...prev.electricity,
                                            amount: {value: e.target.value, error: prev.electricity.amount.error}
                                        }
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label={<span>Electricity unit cost</span>}
                                error={baselineForm.electricity.unitCost.error !== ''}
                                helperText={baselineForm.electricity.unitCost.error}
                                style={{marginBottom:'1.25vh'}}
                                value={baselineForm.electricity.unitCost.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setBaselineForm(prev => ({
                                        ...prev,
                                        electricity: {
                                            ...prev.electricity,
                                            unitCost: {value: e.target.value, error: prev.electricity.unitCost.error}
                                        }
                                    }))
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/kWh</InputAdornment>,
                                }}
                            />

                            <br/>

                            <p>Gas</p>

                            <FormControl fullWidth style={{ width: '100%', marginBottom: '1.25vh' }}>
                                <InputLabel id="demo-simple-select-label">Gas Units</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        defaultValue="m3"
                                        label="Water Units"
                                        value={units}
                                        onChange={handleUnits}
                                        MenuProps={{ disablePortal: true }}
                                    >
                                        <MenuItem value="m3">m<sup>3</sup></MenuItem>
                                        <MenuItem value="usg">USG</MenuItem>
                                        <MenuItem value="mcf">MCF</MenuItem>
                                        <MenuItem value="ccf">CCF</MenuItem>
                                        <MenuItem value="acf">acre-feet</MenuItem>
                                        <MenuItem value="l">L</MenuItem>
                                    </Select>
                            </FormControl>

                            <br/>

                            <TextField
                                fullWidth
                                label={<span>Baseline gas usage</span>}
                                error={baselineForm.gas.amount.error !== ''}
                                helperText={baselineForm.gas.amount.error}
                                style={{marginBottom:'1.25vh'}}
                                value={baselineForm.gas.amount.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setBaselineForm(prev => ({
                                        ...prev,
                                        gas: {
                                            ...prev.gas,
                                            amount: {value: e.target.value, error: prev.gas.amount.error}
                                        }
                                    }))
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">{gasUnitString}</InputAdornment>,
                                }}
                            />

                            <br/>

                            <TextField
                                fullWidth
                                label={<span>Gas unit cost</span>}
                                error={baselineForm.gas.unitCost.error !== ''}
                                helperText={baselineForm.gas.unitCost.error}
                                style={{marginBottom:'1.25vh'}}
                                value={baselineForm.gas.unitCost.value}
                                onChange={e => {
                                    if(!floatRegex.test(e.target.value)) return;

                                    setBaselineForm(prev => ({
                                        ...prev,
                                        gas: {
                                            ...prev.gas,
                                            unitCost: {value: e.target.value, error: prev.gas.unitCost.error}
                                        }
                                    }))
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/{gasUnitString}</InputAdornment>,
                                }}
                            />
                        </>
                    }
                </Modal.Body>
            }
            <Modal.Footer>
                {
                    props.targetModalState === 'delete' ? 
                    <Button size="small" style={{border:'none', outline:'none', backgroundColor:'rgb(213,43,0)'}} onClick={() => {
                        props.deleteTarget();
                    }}>
                        Delete
                    </Button>
                    :
                    <div className="field-row-full">
                        {page > 1 && <IconButton aria-label="site-create-back" onClick={() => setPage(page - 1)}>
                            <ArrowBackIcon />
                        </IconButton>}

                        <div className="field-spacer-lg"></div>

                        <IconButton aria-label="site-create-next" onClick={() => incrementPage()}>
                            {(page === 2 && firstPageValues.entryMethod !== 'manual') || page === 3 ? <DoneIcon /> : <ArrowForwardIcon />}
                        </IconButton>
                    </div>
                }
            </Modal.Footer>
        </Modal>
    );
}

export default TargetModal;